
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { IStatCard } from '@/types/monitor-types'

@Component({ name: 'DashboardStatCell' })
export default class DashboardStatCell extends Vue {
    @Prop({ required: true }) private card!: IStatCard

    /** sparkline 尺寸：宽度跟随容器自适应，高度固定 26 */
    private sparkW: number = 140
    private sparkH: number = 26

    /** resize 防抖句柄 */
    private rzTimer: number | null = null

    /** 入场 / 轮询滚动动画：当前展示的数值（比率卡为 0~1 原始值） */
    private displayValue: number = 0
    private rafId: number | null = null
    private firstDone: boolean = false

    private mounted() {
        // 下一帧等布局完成再读容器宽度
        this.$nextTick(() => this.updateSparkWidth())
        window.addEventListener('resize', this.handleResize, true)
        // 进场从 0 滚动到目标值
        this.$nextTick(() => this.animateTo(this.card.value))
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.handleResize, true)
        if (this.rzTimer !== null) {
            window.clearTimeout(this.rzTimer)
            this.rzTimer = null
        }
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId)
            this.rafId = null
        }
    }

    /** 轮询更新：旧值 → 新值平滑滚动 */
    @Watch('card.value')
    private onValueChange(newVal: number, oldVal: number) {
        this.animateTo(newVal, oldVal)
    }

    /**
     * 数值滚动动画（easeOutCubic）
     * from 缺省时：首帧从 0 滚动（进场）；之后从当前展示值滚动
     * 文档约定：generated_at 与上次相同则不重播；本实现仅在 value 变化时才触发，天然满足
     */
    private animateTo(to: number, from?: number) {
        const start = from !== undefined ? from : (this.firstDone ? this.displayValue : 0)
        const end = to
        const dur = this.firstDone ? 500 : 700
        this.firstDone = true
        if (this.rafId !== null) cancelAnimationFrame(this.rafId)
        const t0 = performance.now()
        const step = (now: number) => {
            const p = Math.min(1, (now - t0) / dur)
            const e = 1 - Math.pow(1 - p, 3)
            this.displayValue = start + (end - start) * e
            if (p < 1) {
                this.rafId = requestAnimationFrame(step)
            } else {
                this.displayValue = end
                this.rafId = null
            }
        }
        this.rafId = requestAnimationFrame(step)
    }

    /** 读取容器实际宽度（扣 8px 让末端点不贴边） */
    private updateSparkWidth() {
        const el = this.$refs.sparkContainer as HTMLElement | undefined
        if (!el) return
        const w = Math.max(60, el.clientWidth - 8)
        if (w !== this.sparkW) {
            this.sparkW = w
        }
    }

    /** resize 时防抖重算 */
    private handleResize = () => {
        if (this.rzTimer !== null) {
            window.clearTimeout(this.rzTimer)
        }
        this.rzTimer = window.setTimeout(() => {
            this.updateSparkWidth()
        }, 180)
    }

    /** 千分位分组 */
    private groupNumber(n: number): string {
        return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    /** 主数字格式化（基于动画中的 displayValue） */
    get formattedValue(): string {
        const v = this.displayValue
        if (this.card.kind === 'ratio') {
            return (v * 100).toFixed(1) + '%'
        }
        return this.groupNumber(v)
    }

    get isNullDelta(): boolean {
        return this.card.delta === null || this.card.delta === undefined
    }

    get deltaTone(): string {
        const d = this.card.delta
        if (this.isNullDelta) return 'is--flat'
        if (d as number > 0) return 'is--up'
        if (d as number < 0) return 'is--down'
        return 'is--flat'
    }

    get deltaSign(): string {
        const d = this.card.delta
        if (this.isNullDelta) return ''
        return (d as number > 0) ? '▲ ' : '▼ '
    }

    get absDelta(): string {
        return this.isNullDelta ? '' : String(Math.abs(this.card.delta as number))
    }

    get deltaUnit(): string {
        return this.card.kind === 'ratio' ? 'pp' : '%'
    }

    /** sparkline polyline points */
    get sparkPoints(): string {
        const data = this.card.sparkline
        const w = this.sparkW
        const h = this.sparkH
        if (!data || data.length === 0) return ''
        const min = Math.min(...data)
        const max = Math.max(...data)
        const rng = (max - min) || 1
        const pts = data.map((v, j) => {
            const x = (j / (data.length - 1)) * (w - 6) + 2
            const y = h - 4 - ((v - min) / rng) * (h - 9)
            return [x, y] as [number, number]
        })
        return pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')
    }

    /** 末端点坐标 */
    get lastPt(): { x: number, y: number } {
        const data = this.card.sparkline
        const w = this.sparkW
        const h = this.sparkH
        if (!data || data.length === 0) return { x: 2, y: h - 4 }
        const min = Math.min(...data)
        const max = Math.max(...data)
        const rng = (max - min) || 1
        const j = data.length - 1
        const v = data[j]
        const x = (j / (data.length - 1)) * (w - 6) + 2
        const y = h - 4 - ((v - min) / rng) * (h - 9)
        return { x, y }
    }

    private go() {
        if (this.card.navTarget) {
            this.$router.push(this.card.navTarget).catch(() => { /* 路由未注册时吞错 */ })
        }
    }
}
